import {
  formatAppArea,
  formatAppCode,
  getScoreLevel,
  INTEREST_AREAS,
  isConsoleAppCode,
  PLAY_THRESHOLDS,
} from "@/v2-backoffice-client/usage/usage.constants"
import { describeTrend } from "@/v2-backoffice-client/usage/usage.text"
import type { UsageAppMixEntry } from "@/v2-backoffice-client/usage/UsageAppMix"
import type { UsageAppSplitEntry } from "@/v2-backoffice-client/usage/usage.utils"

/**
 * One member of a team/customer scope with their usage profile joined on
 * (UsageMemberProfile in the graph). Profile fields are null for members
 * with no tracked activity — dormant seats. Weighted fields and the score
 * are null on rows synced before the weighting rollout.
 */
export type UsageScopeMember = {
  UserId: number
  FirstName?: string | null
  LastName?: string | null
  Email?: string | null
  LastActiveAt?: string | null
  Events7d?: number | null
  Events28d?: number | null
  EventsPrev28d?: number | null
  TrendPct?: number | null
  WeightedEvents28d?: number | null
  WeightedEventsPrev28d?: number | null
  WeightedTrendPct?: number | null
  EngagementScore?: number | null
  ActiveDays28d?: number | null
  DistinctAreas28d?: number | null
  /** 28 weighted daily totals, oldest first (the profile's day strip). */
  Daily28d?: number[]
  TopAreas28d: { Area: string; Count: number; Weighted?: number | null }[]
  AppMix: {
    App: string
    Events28d: number
    Weighted28d: number
    TrendPct?: number | null
    LastActiveAt?: string | null
  }[]
  GeoEvents28d?: number | null
  TopGeoAreas28d: { Code: string; Name: string; Count: number; Pct: number }[]
  PeakEventsPerHour28d?: number | null
  BurstPct28d?: number | null
  SuspectHours28d?: number | null
  MedianPeakGapSeconds?: number | null
  AutomationSuspect: boolean
}

export type ChurnRiskLevel = "high" | "medium" | "low" | "new" | "none"

export const CHURN_RISK_LABELS: Record<ChurnRiskLevel, string> = {
  high: "High risk",
  medium: "Needs attention",
  low: "Healthy",
  new: "No baseline yet",
  none: "No seats",
}

/**
 * Canonical display order of the actionable churn levels, most urgent
 * first. "none" (no seats) is excluded — it never carries a signal.
 */
export const CHURN_LEVEL_ORDER: Exclude<ChurnRiskLevel, "none">[] = [
  "high",
  "medium",
  "low",
  "new",
]

/**
 * Seat counts per engagement band for the stacked license bar. `quiet`
 * covers seats with no activity in the current window (newly-quiet +
 * never-active); the scored bands cover the active seats.
 */
export type UsageSeatBands = {
  power: number
  healthy: number
  cooling: number
  dormant: number
  quiet: number
}

/**
 * Display labels per seat band, shared by the license bar and the Portfolio
 * engagement-distribution chart so both tell the same story.
 */
export const SEAT_BAND_LABELS: Record<keyof UsageSeatBands, string> = {
  power: "Power users",
  healthy: "Healthy",
  cooling: "Cooling",
  dormant: "Barely active",
  quiet: "Idle licenses",
}

export type UsageScopeSummary = {
  seatCount: number
  activeUsers28d: number
  activeUsers7d: number
  seatBands: UsageSeatBands
  /** Active in the previous 28d window but silent in the current one. */
  newlyQuiet: UsageScopeMember[]
  /** No tracked activity in either window (incl. never-tracked seats). */
  dormant: UsageScopeMember[]
  /** Active members whose own weighted trend is falling hard (<= -50%). */
  falling: UsageScopeMember[]
  /** Suspected scripted usage — API-license conversation candidates. */
  automationSuspects: UsageScopeMember[]
  /** Highest weighted-activity active members — renewal anchors. */
  champions: UsageScopeMember[]
  events28d: number
  eventsPrev28d: number
  /** Weighted 28d activity across the scope (null pre-rollout). */
  weightedEvents28d: number | null
  /** Weighted previous-28d activity across the scope (0 pre-rollout). */
  weightedEventsPrev28d: number
  /** Scope-level trend (weighted when available); null without baseline. */
  trendPct: number | null
  /** Mean engagement score over active members; null when nobody scores. */
  avgScore: number | null
  lastActiveAt: string | null
  /** Aggregated member top areas by weighted volume, highest first. */
  topAreas: { area: string; count: number }[]
  /**
   * Aggregated municipalities of geographic interest (members' located
   * events summed per municipality), highest share first. Pct is of the
   * scope's total located events.
   */
  topGeoAreas: { Code: string; Name: string; Count: number; Pct: number }[]
  /** Interest areas nobody in the scope touched — training/module pitches. */
  untouchedAreas: string[]
  churnRisk: { level: ChurnRiskLevel; reasons: string[] }
}

const CHAMPION_LIMIT = 3
const TOP_AREA_LIMIT = 5
const TOP_GEO_AREA_LIMIT = 5

/**
 * Rolls per-member usage profiles up into the team/customer-level summary
 * the Activity tabs render: seat activation, scope trend, churn-risk level
 * with human-readable reasons, and the upsell raw material (top areas,
 * untouched areas, automation suspects, champions). Pure derivation —
 * thresholds live in usage.constants.ts so user- and scope-level signals
 * agree.
 */
export function deriveUsageScopeSummary(
  members: UsageScopeMember[],
): UsageScopeSummary {
  const active = members.filter((m) => (m.Events28d ?? 0) > 0)
  const newlyQuiet = members.filter(
    (m) => (m.Events28d ?? 0) === 0 && (m.EventsPrev28d ?? 0) > 0,
  )
  const dormant = members.filter(
    (m) => (m.Events28d ?? 0) === 0 && (m.EventsPrev28d ?? 0) === 0,
  )
  const falling = active.filter((m) => {
    const trend = m.WeightedTrendPct ?? m.TrendPct
    return trend != null && trend <= -50
  })
  const automationSuspects = active.filter((m) => m.AutomationSuspect)
  const champions = [...active]
    .sort(
      (a, b) =>
        (b.WeightedEvents28d ?? b.Events28d ?? 0) -
        (a.WeightedEvents28d ?? a.Events28d ?? 0),
    )
    .slice(0, CHAMPION_LIMIT)

  const events28d = sum(members.map((m) => m.Events28d ?? 0))
  const eventsPrev28d = sum(members.map((m) => m.EventsPrev28d ?? 0))
  const weightedEvents28d = members.some((m) => m.WeightedEvents28d != null)
    ? sum(members.map((m) => m.WeightedEvents28d ?? 0))
    : null
  const weightedEventsPrev28d = sum(
    members.map((m) => m.WeightedEventsPrev28d ?? 0),
  )

  // Scope trend from the weighted sums when a weighted baseline exists,
  // otherwise from the raw sums (pre-rollout rows).
  const trendPct =
    weightedEvents28d != null && weightedEventsPrev28d > 0
      ? ((weightedEvents28d - weightedEventsPrev28d) / weightedEventsPrev28d) *
        100
      : eventsPrev28d > 0
        ? ((events28d - eventsPrev28d) / eventsPrev28d) * 100
        : null

  const scores = active
    .map((m) => m.EngagementScore)
    .filter((score): score is number => score != null)
  const avgScore =
    scores.length > 0 ? Math.round(sum(scores) / scores.length) : null

  const lastActiveAt = members.reduce<string | null>(
    (latest, m) =>
      m.LastActiveAt && (!latest || m.LastActiveAt > latest)
        ? m.LastActiveAt
        : latest,
    null,
  )

  const seatBands: UsageSeatBands = {
    power: 0,
    healthy: 0,
    cooling: 0,
    dormant: 0,
    quiet: newlyQuiet.length + dormant.length,
  }
  for (const member of active) {
    const level = getScoreLevel(member.EngagementScore)
    // Active-but-unscored seats (pre-rollout rows) read as cooling: they do
    // something, we just can't vouch for how much.
    if (level === "new") seatBands.cooling += 1
    else seatBands[level] += 1
  }

  const areaCounts = new Map<string, number>()
  for (const member of members) {
    for (const { Area, Count, Weighted } of member.TopAreas28d) {
      areaCounts.set(Area, (areaCounts.get(Area) ?? 0) + (Weighted ?? Count))
    }
  }
  const topAreas = [...areaCounts.entries()]
    .map(([area, count]) => ({ area, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_AREA_LIMIT)
  const untouchedAreas = INTEREST_AREAS.filter((area) => !areaCounts.has(area))

  // Members' municipality counts summed per municipality. The pct base is
  // the scope's total located events so shares stay comparable even though
  // each member only reports their own top municipalities.
  const geoCounts = new Map<string, { name: string; count: number }>()
  let geoEvents = 0
  for (const member of members) {
    geoEvents += member.GeoEvents28d ?? 0
    for (const { Code, Name, Count } of member.TopGeoAreas28d) {
      const entry = geoCounts.get(Code)
      if (entry) entry.count += Count
      else geoCounts.set(Code, { name: Name, count: Count })
    }
  }
  const topGeoAreas = [...geoCounts.entries()]
    .map(([code, { name, count }]) => ({
      Code: code,
      Name: name,
      Count: count,
      Pct: geoEvents > 0 ? (count / geoEvents) * 100 : 0,
    }))
    .sort((a, b) => b.Count - a.Count)
    .slice(0, TOP_GEO_AREA_LIMIT)

  return {
    seatCount: members.length,
    activeUsers28d: active.length,
    activeUsers7d: members.filter((m) => (m.Events7d ?? 0) > 0).length,
    seatBands,
    newlyQuiet,
    dormant,
    falling,
    automationSuspects,
    champions,
    events28d,
    eventsPrev28d,
    weightedEvents28d,
    weightedEventsPrev28d,
    trendPct,
    avgScore,
    lastActiveAt,
    topAreas,
    topGeoAreas,
    untouchedAreas,
    churnRisk: deriveChurnRisk({
      seatCount: members.length,
      activeCount: active.length,
      newlyQuietCount: newlyQuiet.length,
      dormantCount: dormant.length,
      fallingCount: falling.length,
      avgScore,
      hasAnyActivity: events28d > 0 || eventsPrev28d > 0,
      trendPct,
    }),
  }
}

type ChurnRiskInput = {
  seatCount: number
  activeCount: number
  newlyQuietCount: number
  dormantCount: number
  fallingCount: number
  /** Mean engagement score over active members; null when unscored. */
  avgScore: number | null
  hasAnyActivity: boolean
  trendPct: number | null
}

/**
 * Rule-based churn-risk level with the triggering observations spelled out,
 * so sales/CS can act on the WHY, not just a color. Deliberately simple and
 * explainable over clever: license conversations start from "3 of 8 seats
 * went quiet", not from an opaque score. Shared by the scope summaries AND
 * the teams/customers table badges so the two never disagree.
 */
function deriveChurnRisk(input: ChurnRiskInput): {
  level: ChurnRiskLevel
  reasons: string[]
} {
  const {
    seatCount,
    activeCount,
    newlyQuietCount,
    dormantCount,
    fallingCount,
    avgScore,
    hasAnyActivity,
    trendPct,
  } = input

  if (seatCount === 0) return { level: "none", reasons: [] }

  // Never-seen scope: no activity in either window across every seat.
  if (!hasAnyActivity) {
    return {
      level: "new",
      reasons: ["No tracked activity from any seat yet."],
    }
  }

  const quietCount = newlyQuietCount + dormantCount
  const quietShare = quietCount / seatCount
  const reasons: string[] = []
  let level: ChurnRiskLevel = "low"

  if (activeCount === 0) {
    level = "high"
    reasons.push("No seat has been active in the last 28 days.")
  }
  if (trendPct != null && trendPct <= -50) {
    level = "high"
    reasons.push(
      `Usage is down ${Math.abs(trendPct).toFixed(0)}% vs the previous 28 days.`,
    )
  } else if (trendPct != null && trendPct <= -20) {
    level = highest(level, "medium")
    reasons.push(
      `Usage is down ${Math.abs(trendPct).toFixed(0)}% vs the previous 28 days.`,
    )
  }
  if (newlyQuietCount > 0) {
    level = highest(
      level,
      newlyQuietCount >= 2 && quietShare >= 0.5 ? "high" : "medium",
    )
    reasons.push(
      `${newlyQuietCount} of ${seatCount} seat${seatCount === 1 ? "" : "s"} went quiet this window.`,
    )
  }
  if (fallingCount > 0) {
    level = highest(level, "medium")
    reasons.push(
      `${fallingCount} active user${fallingCount === 1 ? "" : "s"} with sharply falling usage.`,
    )
  }
  if (dormantCount > 0 && dormantCount / seatCount >= 0.5) {
    level = highest(level, "medium")
    reasons.push(
      `${dormantCount} of ${seatCount} seat${seatCount === 1 ? " has" : "s have"} never been active — unactivated licenses.`,
    )
  }
  if (avgScore != null && avgScore < 25 && activeCount > 0) {
    level = highest(level, "medium")
    reasons.push(
      `Average engagement score across active seats is only ${avgScore}.`,
    )
  }

  if (reasons.length === 0) {
    reasons.push("Activity is steady or growing across the active seats.")
  }
  return { level, reasons }
}

/**
 * Flat usage rollup as the tenants/customers list procs return it
 * (Get_Acc_Tenants / Get_Acc_Customers usageStats OUTER APPLY). All fields
 * are null on reads that don't compute the rollup.
 */
export type UsageScopeStats = {
  UsageSeatCount?: number | null
  UsageActiveCount?: number | null
  UsageNewlyQuietCount?: number | null
  UsageDormantCount?: number | null
  UsageFallingCount?: number | null
  UsageAvgScore?: number | null
  UsageWeightedEvents28d?: number | null
  UsageWeightedEventsPrev28d?: number | null
  UsageEvents28d?: number | null
  UsageEventsPrev28d?: number | null
}

/**
 * Derives the table-facing usage signals (avg score, trend, churn risk) from
 * a list row's flat rollup — same deriveChurnRisk the Activity-tab summary
 * uses, so the badge in the table and the panel never disagree. Returns null
 * when the row carries no rollup.
 */
export function deriveScopeUsageSignals(stats: UsageScopeStats) {
  if (stats.UsageSeatCount == null) return null

  const events28d = stats.UsageEvents28d ?? 0
  const eventsPrev28d = stats.UsageEventsPrev28d ?? 0
  const weighted28d = stats.UsageWeightedEvents28d
  const weightedPrev28d = stats.UsageWeightedEventsPrev28d ?? 0

  const trendPct =
    weighted28d != null && weightedPrev28d > 0
      ? ((weighted28d - weightedPrev28d) / weightedPrev28d) * 100
      : eventsPrev28d > 0
        ? ((events28d - eventsPrev28d) / eventsPrev28d) * 100
        : null

  const avgScore =
    stats.UsageAvgScore != null ? Math.round(stats.UsageAvgScore) : null

  return {
    seatCount: stats.UsageSeatCount,
    activeCount: stats.UsageActiveCount ?? 0,
    avgScore,
    trendPct,
    churnRisk: deriveChurnRisk({
      seatCount: stats.UsageSeatCount,
      activeCount: stats.UsageActiveCount ?? 0,
      newlyQuietCount: stats.UsageNewlyQuietCount ?? 0,
      dormantCount: stats.UsageDormantCount ?? 0,
      fallingCount: stats.UsageFallingCount ?? 0,
      avgScore,
      hasAnyActivity: events28d > 0 || eventsPrev28d > 0,
      trendPct,
    }),
  }
}

/**
 * Rolls the members' per-app usage up to scope level: weighted volume,
 * active seats, and latest activity per console generation, dominant app
 * first. Per-app TREND is taken from the daily rows instead (deriveAppSplit
 * — exact windows); pass its output to merge the trends in.
 */
/**
 * Members with console activity in the last 28 days — the Fusion tab's
 * "seats in use", which must not count a person who only works through an
 * MCP server.
 */
export function countConsoleActiveSeats(members: UsageScopeMember[]) {
  return members.filter((member) =>
    member.AppMix.some(
      (entry) => isConsoleAppCode(entry.App) && entry.Events28d > 0,
    ),
  ).length
}

/**
 * The app codes that carry the last 28 days' activity, for the product tag:
 * at least 1 % of the weighted total, so one stray click in a second
 * product does not make a two-product user.
 */
export function activeApps(appMix: UsageAppMixEntry[]) {
  const total = appMix.reduce((sum, entry) => sum + entry.weighted28d, 0)
  return appMix
    .filter((entry) =>
      total > 0 ? entry.weighted28d >= total / 100 : entry.events28d > 0,
    )
    .map((entry) => entry.app)
}

export function deriveScopeAppMix(
  members: UsageScopeMember[],
  appSplit: UsageAppSplitEntry[] = [],
): UsageAppMixEntry[] {
  const byApp = new Map<
    string,
    {
      weighted28d: number
      events28d: number
      activeSeats: number
      lastActiveAt: string | null
    }
  >()
  for (const member of members) {
    for (const app of member.AppMix) {
      const entry = byApp.get(app.App) ?? {
        weighted28d: 0,
        events28d: 0,
        activeSeats: 0,
        lastActiveAt: null,
      }
      entry.weighted28d += app.Weighted28d
      entry.events28d += app.Events28d
      if (app.Events28d > 0) entry.activeSeats += 1
      if (
        app.LastActiveAt &&
        (!entry.lastActiveAt || app.LastActiveAt > entry.lastActiveAt)
      ) {
        entry.lastActiveAt = app.LastActiveAt
      }
      byApp.set(app.App, entry)
    }
  }

  const trendByApp = new Map(
    appSplit.map((entry) => [entry.app, entry.trendPct]),
  )
  return [...byApp.entries()]
    .map(([app, entry]) => ({
      app,
      weighted28d: entry.weighted28d,
      events28d: entry.events28d,
      activeSeats: entry.activeSeats,
      lastActiveAt: entry.lastActiveAt,
      trendPct: trendByApp.get(app) ?? null,
    }))
    .sort((a, b) => b.weighted28d - a.weighted28d)
}

/**
 * The play's mechanic — what KIND of move this is, independent of the
 * customer-specific sentence. The Portfolio playbook renders these as bold
 * skim-labels so ten accounts can be triaged without reading ten paragraphs.
 */
export type UsageInsightKind =
  | "idle-seats"
  | "newly-quiet"
  | "falling-users"
  | "untouched-areas"
  | "automation"
  | "champions"
  | "steady"
  | "v1-only"
  | "v1-fading"
  | "migration"
  | "v2-pilot"

/** A user called out by name inside an insight text — linkable in the UI. */
export type UsageInsightMention = {
  userId: number
  /** EXACTLY as the name appears in the insight text (substring contract). */
  name: string
}

export type UsageInsight = {
  kind: UsageInsightKind
  tone: "action" | "opportunity" | "positive"
  /** The full play: what is happening and what to do about it. */
  text: string
  /**
   * The play in a few words of data ("9 of 13 seats idle", the names, the
   * areas), for surfaces that show many accounts at once; `text` is the
   * explanation behind it.
   */
  brief: string
  /** Users named in `text` and `brief`, in order of appearance. */
  mentions?: UsageInsightMention[]
}

const UNTOUCHED_AREA_PITCH_LIMIT = 3

function previousWeightedOf(member: UsageScopeMember) {
  return member.WeightedEventsPrev28d ?? member.EventsPrev28d ?? 0
}

/**
 * Turns the scope summary into the plays a sales/CS person should run —
 * plain sentences with names and numbers, ordered by urgency. This is where
 * the "high engagement AND high churn risk" paradox gets spelled out: the
 * score averages the ACTIVE seats, so a few enthusiasts plus a pile of idle
 * licenses reads as both. The renewal risk is the idle licenses (downgrade
 * to fewer seats), and the counter-move is activating them.
 */
export function deriveUsageInsights(
  summary: UsageScopeSummary,
): UsageInsight[] {
  const insights: UsageInsight[] = []
  const T = PLAY_THRESHOLDS
  const weighted = summary.weightedEvents28d ?? summary.events28d
  const previousWeighted = summary.weightedEventsPrev28d
  const quietCount = summary.seatBands.quiet
  const quietShare = summary.seatCount > 0 ? quietCount / summary.seatCount : 0

  if (
    summary.seatCount >= T.idleSeats.minSeats &&
    quietCount >= T.idleSeats.minIdle &&
    quietShare >= T.idleSeats.minIdleShare &&
    summary.activeUsers28d > 0
  ) {
    insights.push({
      kind: "idle-seats",
      tone: "action",
      brief: `${quietCount} of ${summary.seatCount} seats idle`,
      text:
        `Only ${summary.activeUsers28d} of ${summary.seatCount} licenses ${summary.activeUsers28d === 1 ? "is" : "are"} in active use. ` +
        `Engaged users don't protect the renewal while ${quietCount} seat${quietCount === 1 ? "" : "s"} sit idle — ` +
        `the customer can downgrade to fewer licenses. Get the idle seats onboarded before the renewal conversation.`,
    })
  }

  // Only people who did real work before count as gone quiet; one of two
  // seats is half the account, one of ten is a holiday.
  const newlyQuiet = summary.newlyQuiet.filter(
    (member) => previousWeightedOf(member) >= T.newlyQuiet.minPreviousWeighted,
  )
  if (
    newlyQuiet.length > 0 &&
    (newlyQuiet.length >= T.newlyQuiet.minPeople ||
      newlyQuiet.length / Math.max(summary.seatCount, 1) >=
        T.newlyQuiet.minSeatShare)
  ) {
    insights.push({
      kind: "newly-quiet",
      tone: "action",
      brief: nameList(newlyQuiet),
      text:
        `${nameList(newlyQuiet)} ${newlyQuiet.length === 1 ? "was" : "were"} active last period but ` +
        `${newlyQuiet.length === 1 ? "has" : "have"} gone quiet — a check-in now is cheaper than a churned seat at renewal.`,
      mentions: nameMentions(newlyQuiet),
    })
  }

  // A fall counts from a real base, and matters when the account itself is
  // falling or the fallers are a good share of the active people.
  const falling = summary.falling.filter(
    (member) => previousWeightedOf(member) >= T.falling.minPreviousWeighted,
  )
  if (
    falling.length > 0 &&
    ((summary.trendPct ?? 0) <= T.falling.accountTrendPct ||
      falling.length / Math.max(summary.activeUsers28d, 1) >=
        T.falling.minActiveShare)
  ) {
    insights.push({
      kind: "falling-users",
      tone: "action",
      brief: nameList(falling),
      text: `Usage from ${nameList(falling)} is dropping sharply — find out what changed.`,
      mentions: nameMentions(falling),
    })
  }

  if (
    summary.untouchedAreas.length > 0 &&
    summary.activeUsers28d >= T.untouched.minActive &&
    (summary.avgScore ?? 0) >= T.untouched.minScore &&
    summary.topAreas.length >= T.untouched.minUsedAreas &&
    weighted >= T.untouched.minWeighted &&
    summary.churnRisk.level !== "high"
  ) {
    const pitch = summary.untouchedAreas
      .slice(0, UNTOUCHED_AREA_PITCH_LIMIT)
      .map(formatAppArea)
      .join(", ")
    const interest = summary.topAreas[0]
      ? ` — a natural extension of their ${formatAppArea(summary.topAreas[0].area).toLowerCase()} work`
      : ""
    insights.push({
      kind: "untouched-areas",
      tone: "opportunity",
      brief: pitch,
      text: `Nobody has touched ${pitch}. A demo or workshop on these is an expansion angle${interest}.`,
    })
  }

  if (summary.automationSuspects.length > 0) {
    insights.push({
      kind: "automation",
      tone: "opportunity",
      brief: nameList(summary.automationSuspects),
      text: `${nameList(summary.automationSuspects)} show${summary.automationSuspects.length === 1 ? "s" : ""} scripted-usage patterns — open the API-license conversation.`,
      mentions: nameMentions(summary.automationSuspects),
    })
  }

  if (
    summary.trendPct != null &&
    summary.trendPct >= T.champions.minTrendPct &&
    summary.champions.length > 0 &&
    weighted >= T.champions.minWeighted &&
    previousWeighted >= T.champions.minPreviousWeighted
  ) {
    insights.push({
      kind: "champions",
      tone: "positive",
      brief: `${describeTrend(summary.trendPct)} · ${nameList(summary.champions)}`,
      text: `Usage is ${describeTrend(summary.trendPct)} on the four weeks before. Anchor the renewal on ${nameList(summary.champions)} — they can champion an expansion internally.`,
      mentions: nameMentions(summary.champions),
    })
  }

  if (
    insights.length === 0 &&
    summary.activeUsers28d >= T.steady.minActive &&
    (summary.avgScore ?? 0) >= T.steady.minScore &&
    quietShare <= T.steady.maxIdleShare &&
    weighted >= T.steady.minWeighted
  ) {
    insights.push({
      kind: "steady",
      tone: "positive",
      brief: `${summary.activeUsers28d} of ${summary.seatCount} seats active, engagement ${Math.round(summary.avgScore ?? 0)}`,
      text: "Broad, steady usage across the seats — a reference-customer conversation may be worth more than an upsell push right now.",
    })
  }

  return insights
}

/**
 * Product-fit plays from the per-app split. Console v1 and v2 are sold
 * separately, so the mix itself is sales raw material: v1-heavy accounts
 * are v2 demo candidates, v1 fading without v2 pickup is churn (not
 * migration), and early v2 seats inside a v1 account are the migration
 * beachhead to support. Scopes with no v1 history get no callouts — being
 * v2-only is the modern default, not a signal.
 */
export function deriveAppInsights(appMix: UsageAppMixEntry[]): UsageInsight[] {
  const v1 = appMix.find((entry) => entry.app === "v1-console")
  const v2 = appMix.find((entry) => entry.app === "v2-console")
  if (!v1) return []

  const insights: UsageInsight[] = []
  const v1Label = formatAppCode("v1-console")
  const v2Label = formatAppCode("v2-console")
  const v2Weighted = v2?.weighted28d ?? 0
  // What Console v1 did in the window before, read back from the trend:
  // a handful of clicks collapsing to one is not a product moving.
  const v1Previous =
    v1.trendPct != null && v1.trendPct > -100
      ? v1.weighted28d / (1 + v1.trendPct / 100)
      : null
  const v1Fading =
    v1.trendPct != null &&
    v1.trendPct <= -30 &&
    (v1Previous == null || v1Previous >= PLAY_THRESHOLDS.consoleFloor)
  const v2Active = v2Weighted > 0
  const v2Growing =
    v2Active && (v2?.trendPct == null || (v2.trendPct ?? 0) >= 20)
  const v2Carries = v2Weighted >= v1.weighted28d

  if (
    v1.weighted28d >= PLAY_THRESHOLDS.v1Only.minWeighted &&
    (v1.activeSeats ?? 0) >= PLAY_THRESHOLDS.v1Only.minActiveSeats &&
    !v2Active
  ) {
    const seats =
      v1.activeSeats != null
        ? `${v1.activeSeats} active ${v1Label} seat${v1.activeSeats === 1 ? "" : "s"}`
        : `their ${v1Label} usage`
    insights.push({
      kind: "v1-only",
      tone: "opportunity",
      brief:
        v1.activeSeats != null
          ? `${v1.activeSeats} active ${v1Label} seat${v1.activeSeats === 1 ? "" : "s"}, none in ${v2Label}`
          : `${v1Label} only`,
      text: `All console usage is in ${v1Label} — ${v2Label} is untouched. ${capitalize(seats)} make${v1.activeSeats === 1 ? "s" : ""} a warm audience for a ${v2Label} demo.`,
    })
  }

  // Fading v1 is disengagement only while v1 is still the console in use;
  // once v2 carries the work, the account has moved.
  if (v1Fading && !v2Growing && !v2Carries) {
    insights.push({
      kind: "v1-fading",
      tone: "action",
      brief: `${v1Label} down ${Math.abs(v1.trendPct ?? 0).toFixed(0)} %, no ${v2Label} pickup`,
      text: `${v1Label} usage is down ${Math.abs(v1.trendPct ?? 0).toFixed(0)}% with no ${v2Label} pickup — that reads as disengagement, not migration. Check in before renewal.`,
    })
  }

  if (v1Fading && v2Growing) {
    insights.push({
      kind: "migration",
      tone: "positive",
      brief: `${v1Label} → ${v2Label} under way`,
      text: `Usage is shifting from ${v1Label} to ${v2Label} — a migration under way. Make sure the ${v2Label} onboarding sticks so it lands as an upgrade, not churn.`,
    })
  }

  if (
    !v1Fading &&
    v1.weighted28d >= PLAY_THRESHOLDS.consoleFloor &&
    v2Weighted >= PLAY_THRESHOLDS.consoleFloor &&
    !v2Carries
  ) {
    const seats =
      v2?.activeSeats != null && v2.activeSeats > 0
        ? `${v2.activeSeats} seat${v2.activeSeats === 1 ? "" : "s"} already active in ${v2Label}`
        : `${v2Label} activity is starting`
    insights.push({
      kind: "v2-pilot",
      tone: "opportunity",
      brief:
        v2?.activeSeats != null && v2.activeSeats > 0
          ? `${v2.activeSeats} seat${v2.activeSeats === 1 ? "" : "s"} in ${v2Label}, ${v1Label} still ahead`
          : `${v2Label} starting, ${v1Label} still ahead`,
      text: `${capitalize(seats)} while ${v1Label} still carries most of the usage — support these pilot users, they anchor the migration story.`,
    })
  }

  return insights
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const NAME_LIST_LIMIT = 3

/**
 * The linkable mentions for a member call-out — the SAME first members, in
 * the SAME order and with the SAME name strings as {@link nameList} renders,
 * so a mention is always an exact substring of the insight text.
 */
function nameMentions(members: UsageScopeMember[]): UsageInsightMention[] {
  return members.slice(0, NAME_LIST_LIMIT).map((member) => ({
    userId: member.UserId,
    name: formatMemberName(member),
  }))
}

function nameList(members: UsageScopeMember[]) {
  const named = nameMentions(members).map((mention) => mention.name)
  const overflow = members.length - named.length
  return overflow > 0
    ? `${named.join(", ")} +${overflow} more`
    : named.join(", ")
}

const RISK_ORDER: ChurnRiskLevel[] = ["none", "new", "low", "medium", "high"]

function highest(a: ChurnRiskLevel, b: ChurnRiskLevel): ChurnRiskLevel {
  return RISK_ORDER.indexOf(a) >= RISK_ORDER.indexOf(b) ? a : b
}

function sum(values: number[]) {
  return values.reduce((total, value) => total + value, 0)
}

/** Short display name for member call-outs ("Ola N.", falls back to email). */
function formatMemberName(member: UsageScopeMember) {
  const first = member.FirstName?.trim()
  const last = member.LastName?.trim()
  if (first || last) {
    return [first, last ? `${last[0]}.` : null].filter(Boolean).join(" ")
  }
  return member.Email ?? `User ${member.UserId}`
}
